// ============================================================
// Movement on the map, memoized - see movement.js for the rules
// ============================================================

import { useCallback, useMemo } from 'react';
import {
  rulesByCombatant,
  walkOf,
  movementOf,
  levelsFor,
  levelCostsFor,
  placeOnLevel,
  reachFor,
  movementBandsFor,
  routeVectorFor,
  climbFactsFor,
  legendClimbFor,
} from './movement';

/**
 * Each combatant's terrain rules and Climb rating, and how a token crosses
 * the map. `rulesForRef` is the marker drag's view of `rulesFor`: the drag
 * hook is created before this, so it reads through the ref.
 */
export function useMovementRules(
  { roster, ignoreTerrain, solids, isGm, groundAt, climbCosts, climbTraining },
  rulesForRef,
) {
  const rulesFor = useMemo(() => rulesByCombatant(roster), [roster]);
  rulesForRef.current = rulesFor;
  // The sheet has carried the Climb rating all along - read straight off
  // the roster, so nothing new is stored.
  const climbSkillOf = useCallback((id) => roster.find((c) => c.id === id)?.sheet?.skills?.climb, [roster]);
  const walkFor = useCallback(
    (token) =>
      walkOf(token, { rulesFor, ignoreTerrain, solids, isGm, groundAt, climbCosts, climbTraining, climbSkillOf }),
    [rulesFor, ignoreTerrain, solids, isGm, groundAt, climbCosts, climbTraining, climbSkillOf],
  );
  return { rulesFor, climbSkillOf, walkFor };
}

/** The sheet's movement of whoever is moving: the actor's and the target's come in hand, anyone else's off the roster. */
function sheetMovementOf(movingId, { actorId, targetId, actorMovement, targetMovement, roster }) {
  if (movingId === actorId) return actorMovement;
  if (movingId === targetId) return targetMovement;
  return roster.find((c) => c.id === movingId)?.sheet?.movement;
}

/**
 * WHOEVER IS CARRIED is the one whose movement is shown - the reach, the
 * route and its band - whoever they are (2026-11-04: in Stealth, with no
 * turns, the GM drags figures that are nobody's actor, and saw no hint).
 * With nobody carried, whoever is SELECTED, not only the combatant you are
 * acting as: a vehicle is almost always someone's target rather than their
 * actor, and gating on `actorId` meant vehicles never got a movement preview.
 */
export function useMovingSelection(props) {
  const { actorId, targetId, tokens, roster, applyConditionModifiers, fatigueSlowsMovement, carriedId } = props;
  const movingId = carriedId || actorId || targetId;
  const sheetMovement = sheetMovementOf(movingId, props);
  const moving = useMemo(
    () => movementOf(movingId, sheetMovement, { roster, applyConditionModifiers, fatigueSlowsMovement }),
    [movingId, sheetMovement, roster, applyConditionModifiers, fatigueSlowsMovement],
  );
  // Whose column it is - the level picker sits away from their marker and must say so.
  const movingName = useMemo(() => roster.find((c) => c.id === movingId)?.name, [roster, movingId]);
  const movingToken = movingId ? tokens[movingId] : null;
  return { movingId, movingToken, moving, movingMovement: moving.movement, movingName };
}

/** What each storey of the column costs to reach. */
function useLevelCosts(m, levels) {
  const { movingToken, levelPickerSpends, climbCosts, climbTraining } = m;
  const { climbSkillOf, rulesFor, movementMode, movingMovement } = m;
  return useMemo(
    () =>
      levelCostsFor({
        movingToken,
        levels,
        levelPickerSpends,
        climbCosts,
        climbTraining,
        climbSkillOf,
        rulesFor,
        movementMode,
        movingMovement,
      }),
    [
      movingToken,
      levels,
      levelPickerSpends,
      climbCosts,
      climbTraining,
      climbSkillOf,
      rulesFor,
      movementMode,
      movingMovement,
    ],
  );
}

/** The storeys of the moving marker's column, what each costs, and the pick. */
export function useLevels(m) {
  const { movingToken, solids, groundAt, walkFor, writeToken, reportClimbRef } = m;
  const levels = useMemo(
    () => levelsFor(movingToken, solids, groundAt, walkFor),
    [movingToken, solids, groundAt, walkFor],
  );
  const levelCosts = useLevelCosts(m, levels);
  const pickLevel = useCallback(
    (level, { free } = {}) => placeOnLevel(level, free, { movingToken, levelCosts, writeToken, reportClimbRef }),
    [movingToken, writeToken, levelCosts, reportClimbRef],
  );
  return { levels, levelCosts, pickLevel };
}

/** The reach disc, its bands, and the route of the marker being dragged. */
export function useReach(m) {
  const { movementMode, movingToken, movingId, movingMovement, dragOrigin, boundary, walkFor, tokens } = m;
  const reach = useMemo(
    () => reachFor({ movementMode, movingToken, movingId, movingMovement, dragOrigin, boundary, walkFor }),
    [movementMode, movingToken, movingId, movingMovement, dragOrigin, boundary, walkFor],
  );
  const movementBands = useMemo(() => movementBandsFor(movementMode, movingMovement), [movementMode, movingMovement]);
  const vector = useMemo(
    () => routeVectorFor({ dragOrigin, movementMode, movingId, movingMovement, tokens, boundary, walkFor }),
    [dragOrigin, movementMode, movingId, movingMovement, tokens, boundary, walkFor],
  );
  return { reach, movementBands, vector };
}

/** What the climb is costing mid-route, and the Legend's climbing page. */
export function useClimbNotes(m) {
  const { vector, movingId, actorId, tokens, walkFor, climbSkillOf, climbCosts, climbTraining, rulesFor } = m;
  const climbFacts = useMemo(
    () => climbFactsFor(vector, movingId, { tokens, walkFor, climbSkillOf, climbCosts, climbTraining, rulesFor }),
    [vector, movingId, tokens, walkFor, climbSkillOf, climbCosts, climbTraining, rulesFor],
  );
  const legendClimb = useMemo(
    () => legendClimbFor(movingId || actorId, { rulesFor, climbSkillOf, climbCosts, climbTraining }),
    [movingId, actorId, rulesFor, climbSkillOf, climbCosts, climbTraining],
  );
  return { climbFacts, legendClimb };
}
